// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useEffect, useState } from "react";
import { Empty, Input, Modal } from "antd";
import { searchMessages, type IndexedMessage } from "../searchIndex";

// Search is necessarily client-side for E2E content: the homeserver only holds
// ciphertext, so it cannot index encrypted messages. This queries a persisted
// per-device index (IndexedDB) of decrypted bodies — spanning ALL history this
// device has ever seen, not just what's in memory.

function localpart(userID: string): string {
  return userID.replace(/^@/, "").split(":")[0];
}

export function SearchModal({
  open,
  onClose,
  onOpen,
  inline = false,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onOpen: (roomID: string) => void;
  // inline renders the body without the Modal wrapper (a mobile bottom-nav screen).
  readonly inline?: boolean;
}) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<IndexedMessage[]>([]);
  const query = q.trim();
  const active = inline || open;

  // Query the index as the user types (debounced), and only while active.
  useEffect(() => {
    if (!active || query.length < 2) {
      setHits([]);
      return;
    }
    let live = true;
    const t = setTimeout(() => {
      void searchMessages(query).then((r) => {
        if (live) setHits(r);
      });
    }, 120);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [active, query]);

  function openHit(roomID: string) {
    onOpen(roomID);
    if (!inline) onClose();
  }

  const body = (
    <>
      <Input.Search placeholder="Search your history…" value={q} onChange={(e) => setQ(e.target.value)} autoFocus={!inline} allowClear />
      <div className="search-results">
        {query.length >= 2 && hits.length === 0 && <Empty description="No matches in your history" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
        {hits.map((h) => (
          <button key={h.eventId} type="button" className="search-hit" onClick={() => openHit(h.roomId)}>
            <span className="search-hit-meta">
              #{h.roomName} · {localpart(h.sender)}
            </span>
            <span className="search-hit-body">{h.body}</span>
          </button>
        ))}
      </div>
    </>
  );

  if (inline) return <div className="mobile-tab-view mobile-search">{body}</div>;
  return (
    <Modal title="Search messages" open={open} onCancel={onClose} footer={null}>
      {body}
    </Modal>
  );
}
